import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { useProCatalog, type ProCatalogEntry } from '@/hooks/use-pro-catalog';
import { PdfThumbnail } from '@/components/catalogue/PdfThumbnail';
import { PdfDocumentView } from '@/components/catalogue/PdfDocumentView';

function formatSize(bytes: number): string {
  if (!bytes) return '';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function CataloguePage() {
  const { t } = useTranslation();
  const catalog = useProCatalog();
  const [searchParams, setSearchParams] = useSearchParams();

  const entries = useMemo(() => catalog.data ?? [], [catalog.data]);
  const openId = searchParams.get('doc');
  const [unreadable, setUnreadable] = useState<string | null>(null);

  const active: ProCatalogEntry | null = useMemo(
    () => entries.find((e) => e.id === openId) ?? null,
    [entries, openId],
  );

  function open(entry: ProCatalogEntry) {
    setSearchParams({ doc: entry.id });
  }

  function close() {
    setSearchParams({}, { replace: true });
  }

  if (catalog.isLoading) {
    return (
      <div className="pt-2 grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-2xl animate-pulse"
            style={{
              aspectRatio: '3 / 4',
              background: 'var(--pro-grey-subtle, #f1f5f9)',
            }}
          />
        ))}
      </div>
    );
  }

  if (catalog.isError) {
    return (
      <EmptyState
        icon={<FileText className="w-7 h-7" />}
        title={t('pro.catalogue.error', 'Catalogue indisponible')}
        hint={t(
          'pro.catalogue.error_hint',
          'Impossible de charger le catalogue pour le moment. Réessayez plus tard.',
        )}
      />
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        icon={<FolderOpen className="w-7 h-7" />}
        title={t('pro.catalogue.empty', 'Aucun catalogue publié')}
        hint={t(
          'pro.catalogue.empty_hint',
          "Les catalogues partagés par votre fournisseur apparaîtront ici dès qu'ils seront publiés.",
        )}
      />
    );
  }

  if (active) {
    const readable = active.isPdf && unreadable !== active.id;

    return (
      <div className="pt-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-white text-[13px] font-semibold hover:bg-neutral-50 transition-colors shrink-0"
              style={{
                border: '1px solid var(--pro-border)',
                color: 'var(--pro-text-dark)',
                fontFamily: 'Figtree',
              }}
            >
              <ArrowLeft className="w-4 h-4 rtl:-scale-x-100" />
              {t('pro.catalogue.back', 'Tous les catalogues')}
            </button>
            <div className="min-w-0">
              <div
                className="text-[15px] font-semibold truncate"
                style={{
                  color: 'var(--pro-text-dark)',
                  fontFamily: 'Figtree',
                }}
              >
                {active.name}
              </div>
              {active.folderName && (
                <div
                  className="text-[12px] truncate"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {active.folderName}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <a
              href={active.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-white text-[13px] font-semibold hover:bg-neutral-50 transition-colors"
              style={{
                border: '1px solid var(--pro-border)',
                color: 'var(--pro-text-dark)',
                fontFamily: 'Figtree',
              }}
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">
                {t('pro.catalogue.open_tab', 'Ouvrir')}
              </span>
            </a>
            <a
              href={active.url}
              download={active.name}
              className="inline-flex items-center gap-2 h-10 px-3 rounded-xl text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{
                background: 'var(--pro-primary)',
                fontFamily: 'Figtree',
              }}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">
                {t('pro.catalogue.download', 'Télécharger')}
              </span>
            </a>
          </div>
        </div>

        {readable ? (
          <PdfDocumentView
            key={active.id}
            url={active.url}
            onFailed={() => setUnreadable(active.id)}
          />
        ) : active.isPdf ? (
          // pdf.js n'a pas su ouvrir ce fichier : plutôt que rien, on repasse
          // au lecteur du navigateur — au pire l'utilisateur y voit la
          // première page, comme avant.
          <div
            className="relative rounded-2xl overflow-hidden bg-white"
            style={{ border: '1px solid var(--pro-border)' }}
          >
            <iframe
              src={active.url}
              title={active.name}
              className="w-full block"
              style={{ height: 'min(78vh, 900px)', border: 0 }}
            />
          </div>
        ) : (
          <EmptyState
            icon={<FileText className="w-7 h-7" />}
            title={t('pro.catalogue.not_previewable', 'Aperçu indisponible')}
            hint={t(
              'pro.catalogue.not_previewable_hint',
              'Ce document ne peut pas être affiché ici. Utilisez « Ouvrir » ou « Télécharger ».',
            )}
          />
        )}
      </div>
    );
  }

  return (
    <div className="pt-2 grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {entries.map((entry) => (
        <button
          key={entry.id}
          type="button"
          onClick={() => open(entry)}
          className="group text-left rounded-2xl bg-white overflow-hidden flex flex-col hover:shadow-md transition-shadow"
          style={{ border: '1px solid var(--pro-border)' }}
        >
          <span
            className="relative block w-full overflow-hidden"
            style={{
              aspectRatio: '3 / 4',
              background: 'var(--pro-grey-subtle, #f7f7f7)',
            }}
          >
            {entry.isPdf ? (
              <PdfThumbnail url={entry.url} />
            ) : (
              <span
                className="absolute inset-0 flex items-center justify-center"
                style={{ color: 'var(--pro-text-grey-light)' }}
              >
                <FileText className="w-8 h-8" />
              </span>
            )}
          </span>
          <span className="block p-3 min-w-0">
            <span
              className="block text-[14px] font-semibold truncate"
              style={{
                color: 'var(--pro-text-dark)',
                fontFamily: 'Figtree',
              }}
            >
              {entry.name}
            </span>
            <span
              className="block text-[12px] truncate"
              style={{ color: 'var(--pro-text-grey)' }}
            >
              {[entry.folderName, formatSize(entry.sizeBytes)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <div
      className="rounded-2xl bg-white py-14 px-6 text-center"
      style={{ border: '1px solid var(--pro-border)' }}
    >
      <div
        className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center"
        style={{
          background: 'var(--pro-grey-subtle, #f1f5f9)',
          color: 'var(--pro-text-grey-light)',
        }}
      >
        {icon}
      </div>
      <p
        className="text-[15px] font-semibold"
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {title}
      </p>
      <p
        className="text-[13px] mt-1 max-w-sm mx-auto"
        style={{ color: 'var(--pro-text-grey)' }}
      >
        {hint}
      </p>
    </div>
  );
}
